/* eslint-disable @next/next/no-img-element */
import { useEffect, useRef, useState } from 'react';
import { BlockProps } from '@/components/Block/Block';
import P from '@/components/Redactie/P/P';
import Resizer from 'react-image-file-resizer';
import { mdiCamera, mdiClose, mdiFolderUpload } from '@mdi/js';
import TextButton from '@/components/Button/TextButton';
import { useBlockLocalUserSingleLargeValue } from '@/hooks/useBlockLocalUserData';
import EmojiText from '@/components/EmojiText/EmojiText';
import { Dialog } from '@headlessui/react';
import Webcam from 'react-webcam';
import { motion, useReducedMotion } from 'framer-motion';

import styles from './PhotoFrameBlock.module.sass';

interface PhotoFrameBlockData {
    title: string;
}

const PhotoFrameBlock = ({
    subjectId,
    id,
    data,
    uuid,
    isAnimated,
    isEditable,
    isEditorPreview,
    onContentHeightChanged,
    onEdited,
    onFinished,
}: BlockProps) => {
    const [blockData, setBlockData] = useState<PhotoFrameBlockData>({
        title: data.title ?? 'Voeg een titel toe',
    });

    if (isEditable)
        return (
            <PhotoFrameBlockEditor
                uuid={uuid}
                blockData={blockData}
                setBlockData={setBlockData}
                onEdited={onEdited}
            />
        );
    else
        return (
            <PhotoFrameBlockViewer
                subjectId={subjectId}
                id={id}
                blockData={blockData}
                isAnimated={isAnimated}
                isEditorPreview={isEditorPreview}
                onContentHeightChanged={onContentHeightChanged}
                onFinished={onFinished}
            />
        );
};

const PhotoFrameBlockEditor = ({
    uuid,
    blockData,
    setBlockData,
    onEdited,
}: {
    uuid: BlockProps['uuid'];
    blockData: PhotoFrameBlockData;
    setBlockData: (data: PhotoFrameBlockData) => void;
    onEdited: BlockProps['onEdited'];
}) => {
    useEffect(() => {
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: {},
        });
    }, [blockData]);

    return (
        <div
            className={`${styles['photoframeblock']} ${styles['photoframeblock--editable']}`}
        >
            <P
                isEditable
                rows={1}
                value={blockData.title}
                onChange={(value) => {
                    setBlockData({
                        ...blockData,
                        title: value,
                    });
                }}
                placeholder="Titel bij fotolijst"
            />
        </div>
    );
};

const PhotoFrameBlockViewer = ({
    subjectId,
    id,
    blockData,
    isAnimated,
    isEditorPreview,
    onContentHeightChanged,
    onFinished,
}: {
    subjectId: BlockProps['subjectId'];
    id: BlockProps['id'];
    blockData: PhotoFrameBlockData;
    isAnimated: BlockProps['isAnimated'];
    isEditorPreview: BlockProps['isEditorPreview'];
    onContentHeightChanged: BlockProps['onContentHeightChanged'];
    onFinished: BlockProps['onFinished'];
}) => {
    const reduceMotion = useReducedMotion();

    const [hasFinished, setHasFinished] = useState(false);

    const {
        value: photoBase64,
        set: setPhotoBase64,
    } = useBlockLocalUserSingleLargeValue(
        subjectId,
        id,
        isEditorPreview,
        'photo',
    );

    useEffect(() => {
        if (!!photoBase64 && !hasFinished) {
            setHasFinished(true);
            onFinished?.();
        }
    }, [photoBase64, hasFinished]);

    return (
        <div
            className={`${styles['photoframeblock']} ${
                isAnimated ? styles['photoframeblock--animated'] : ''
            }`}
        >
            <p className={styles['photoframeblock__title']}>
                <EmojiText>{blockData.title}</EmojiText>
            </p>
            {!!photoBase64 && (
                <motion.section
                    key={photoBase64}
                    aria-label="Foto"
                    className={styles['photoframeblock__photocontainer']}
                    initial={{
                        opacity: 0,
                        height: reduceMotion ? 'auto' : 0,
                    }}
                    animate={{
                        opacity: 1,
                        height: 'auto',
                        transition: {
                            ease: 'easeOut',
                        },
                    }}
                    onAnimationComplete={() => {
                        onContentHeightChanged?.();
                    }}
                >
                    <motion.img
                        key={photoBase64}
                        src={photoBase64}
                        alt="Jouw gekozen foto"
                        className={
                            styles['photoframeblock__photocontainer__photo']
                        }
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: 1,
                            transition: { duration: 1 },
                        }}
                        onAnimationComplete={() => {
                            onContentHeightChanged?.();
                        }}
                    />
                    <div
                        className={
                            styles['photoframeblock__photocontainer__bottom']
                        }
                    />
                </motion.section>
            )}
            <div className={styles['photoframeblock__buttons']}>
                <PhotoWebcamButton
                    photoBase64={photoBase64}
                    setPhotoBase64={setPhotoBase64}
                />
                of
                <PhotoUploadButton
                    photoBase64={photoBase64}
                    setPhotoBase64={setPhotoBase64}
                />
            </div>
        </div>
    );
};

const PhotoUploadButton = ({
    photoBase64,
    setPhotoBase64,
}: {
    photoBase64?: string;
    setPhotoBase64: (uri: string) => void;
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    return (
        <>
            <input
                tabIndex={-1}
                className="hidden"
                multiple={false}
                ref={inputRef}
                type="file"
                onChange={(event) => {
                    if (event.target.files?.length === 1)
                        try {
                            Resizer.imageFileResizer(
                                event.target.files[0],
                                512,
                                512,
                                'JPEG',
                                100,
                                0,
                                (uri) => {
                                    setPhotoBase64(uri as string);
                                },
                                'base64',
                                128,
                                128,
                            );
                        } catch (err) {
                            console.error(err);
                            alert(
                                'Er ging iets mis met het uploaden, probeer het opnieuw of met een andere foto.',
                            );
                        }
                    else {
                        alert('Kies één foto');
                    }
                }}
                accept="image/*"
            />
            <TextButton
                icon={mdiFolderUpload}
                isPrimary={false}
                isSmall={!!photoBase64}
                onClick={() => {
                    inputRef.current?.click();
                }}
                className={styles['photoframeblock__buttons__button--light']}
            >
                Foto kiezen
            </TextButton>
        </>
    );
};

const PhotoWebcamButton = ({
    photoBase64,
    setPhotoBase64,
}: {
    photoBase64?: string;
    setPhotoBase64: (uri: string) => void;
}) => {
    const webcamRef = useRef<Webcam | null>(null);
    const [isWebcamOpened, setIsWebcamOpened] = useState(false);

    return (
        <>
            <TextButton
                icon={mdiCamera}
                isPrimary={!photoBase64}
                isSmall={!!photoBase64}
                onClick={() => {
                    setIsWebcamOpened(true);
                }}
                className={
                    photoBase64
                        ? styles['photoframeblock__buttons__button--light']
                        : undefined
                }
            >
                Foto maken
            </TextButton>
            <Dialog
                open={isWebcamOpened}
                onClose={() => setIsWebcamOpened(false)}
            >
                <motion.div
                    className={styles['photoframeblock__cameradialog__overlay']}
                    aria-hidden="true"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => setIsWebcamOpened(false)}
                />

                <Dialog.Panel
                    className={styles['photoframeblock__cameradialog__main']}
                >
                    <div
                        className={
                            styles[
                                'photoframeblock__cameradialog__main__container'
                            ]
                        }
                    >
                        <Dialog.Title
                            className={
                                styles[
                                    'photoframeblock__cameradialog__main__container__title'
                                ]
                            }
                        >
                            Camera
                        </Dialog.Title>
                        <Webcam
                            ref={webcamRef}
                            mirrored
                            audio={false}
                            screenshotFormat="image/jpeg"
                            className="rounded-xl w-full md:max-w-[50vw] max-h-[50vh]"
                            aria-label="Camerabeeld"
                            videoConstraints={{
                                width: 1280,
                                height: 720,
                                facingMode: 'user',
                            }}
                            onUserMediaError={(err) => {
                                console.error(err);
                                alert(
                                    'Er ging iets mis met het openen van de webcam, probeer het opnieuw of met een andere foto.',
                                );
                            }}
                        />
                        <div
                            className={
                                styles[
                                    'photoframeblock__cameradialog__main__container__buttons'
                                ]
                            }
                        >
                            <TextButton
                                icon={mdiCamera}
                                isPrimary={true}
                                onClick={() => {
                                    if (webcamRef.current) {
                                        const photo = webcamRef.current.getScreenshot();
                                        if (photo !== null) {
                                            try {
                                                fetch(photo)
                                                    .then((res) => res.blob())
                                                    .then((photoAsBlob) => {
                                                        Resizer.imageFileResizer(
                                                            photoAsBlob,
                                                            512,
                                                            512,
                                                            'JPEG',
                                                            100,
                                                            0,
                                                            (uri) => {
                                                                setPhotoBase64(
                                                                    uri as string,
                                                                );
                                                            },
                                                        );
                                                    });
                                            } catch (err) {
                                                console.error(err);
                                                alert(
                                                    'Er ging iets mis met het maken van de foto, probeer het opnieuw of met een andere foto.',
                                                );
                                            }
                                            setIsWebcamOpened(false);
                                        } else {
                                            alert(
                                                'Er ging iets mis met het maken van de foto, probeer het opnieuw of met een andere foto.',
                                            );
                                        }
                                    }
                                }}
                            >
                                Foto maken
                            </TextButton>
                            <TextButton
                                icon={mdiClose}
                                isPrimary={false}
                                onClick={() => {
                                    setIsWebcamOpened(false);
                                }}
                                className={`${styles['photoframeblock__buttons__button--light']} border-none bg-white`}
                            >
                                Annuleren
                            </TextButton>
                        </div>
                    </div>
                </Dialog.Panel>
            </Dialog>
        </>
    );
};

export default PhotoFrameBlock;
